import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Eraser, Check } from 'lucide-react';

const SignaturePad = forwardRef(({ onSignatureChange, width = 400, height = 200 }, ref) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    useImperativeHandle(ref, () => ({
        clear: clearCanvas,
        getSignatureData: getSignatureData,
        isEmpty: () => !hasSignature
    }));

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set up canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw signature line
        drawSignatureLine(ctx);
    }, []);

    const drawSignatureLine = (ctx) => {
        ctx.save();
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(20, height - 40);
        ctx.lineTo(width - 20, height - 40);
        ctx.stroke();
        ctx.restore();

        // Label
        ctx.save();
        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#94a3b8';
        ctx.fillText('Firma aquí', 20, height - 20);
        ctx.restore();
    };

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (e.touches) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const coords = getCoordinates(e);

        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const coords = getCoordinates(e);

        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();

        if (!hasSignature) {
            setHasSignature(true);
        }
    };

    const stopDrawing = () => {
        if (isDrawing) {
            setIsDrawing(false);
            if (onSignatureChange) {
                onSignatureChange(getSignatureData());
            }
        }
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        drawSignatureLine(ctx);
        setHasSignature(false);

        if (onSignatureChange) {
            onSignatureChange(null);
        }
    };

    const getSignatureData = () => {
        if (!hasSignature) return null;
        const canvas = canvasRef.current;
        return canvas.toDataURL('image/png');
    };

    return (
        <div className="signature-pad-container">
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    cursor: 'crosshair',
                    touchAction: 'none',
                    maxWidth: '100%'
                }}
            />
            <div className="signature-actions">
                <button
                    type="button"
                    onClick={clearCanvas}
                    className="btn-clear"
                >
                    <Eraser size={16} />
                    Limpiar
                </button>
                {hasSignature && (
                    <span className="signature-status">
                        <Check size={16} />
                        Firma capturada
                    </span>
                )}
            </div>

            <style>{`
                .signature-pad-container {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .signature-actions {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .btn-clear {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 8px;
                    color: #64748b;
                    font-size: 0.875rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-clear:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .signature-status {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #00BBBF;
                    font-size: 0.875rem;
                    font-weight: 500;
                }
            `}</style>
        </div>
    );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
